import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface PresenceUser {
  user_id: string;
  online_at: string;
}

export function useGlobalPresence() {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [isTracking, setIsTracking] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setOnlineUsers([]);
      setIsTracking(false);
      return;
    }

    const channel = supabase.channel('app-presence', {
      config: { presence: { key: user.id } }
    });

    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<PresenceUser>();
        const userIds = Object.keys(state);
        setOnlineUsers(userIds);
      })
      .on('presence', { event: 'join' }, ({ key }) => {
        setOnlineUsers(prev => {
          if (!prev.includes(key)) {
            return [...prev, key];
          }
          return prev;
        });
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        setOnlineUsers(prev => prev.filter(id => id !== key));
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: user.id,
            online_at: new Date().toISOString(),
          });
          setIsTracking(true);
        }
      });

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      setIsTracking(false);
    };
  }, [user?.id]);

  const isUserOnline = useCallback((userId: string): boolean => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  return {
    onlineUsers,
    isUserOnline,
    onlineCount: onlineUsers.length,
    isTracking,
  };
}
